import { IDocument, IKeyTerm } from '@dealpoint/types'
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { head, pullAll, sortBy, take, uniqBy } from 'lodash'
import { batch } from 'react-redux'

import { APIClient } from '../APIClient'
import { Utils } from '../Utils'
import { IPdfViewerAnnotation } from '../hooks/usePdfViewer/types'

// eslint-disable-next-line import/no-cycle
import { RootState } from './types'

export type ComparisonDocument = Pick<IDocument, 'id' | 'latestVersion' | 'title'>
export type ViewerId = string

interface ComparisonKeyTerm {
  documentId: string
  keyTerm: IKeyTerm & { title: string }
  annotation: IPdfViewerAnnotation
}

export interface ComparisonTermType {
  documentIds: string[]
  id: string
  title: string
}

interface Meta {
  loadingKeyTerms: boolean
  maxViewerCount: number
  minViewerCount: number
}

interface IDocumentCompareState {
  documents: {
    allIds: string[]
    byId: Record<string, ComparisonDocument>
    byViewerId: Record<ViewerId, string>
  }
  keyTerms: {
    byId: Record<string, ComparisonKeyTerm>
    byDocumentId: Record<string, string[]>
    byTermId: Record<string, string[]>
  }
  meta: Meta
  terms: {
    byId: Record<string, ComparisonTermType>
    byDocumentId: Record<string, string[]>
    search: string
    selectedId: string
  }
  viewerIds: ViewerId[]
}

const initialState: IDocumentCompareState = {
  documents: {
    allIds: [],
    byId: {},
    byViewerId: {},
  },
  keyTerms: {
    byId: {},
    byDocumentId: {},
    byTermId: {},
  },
  meta: {
    loadingKeyTerms: false,
    maxViewerCount: 10,
    minViewerCount: 2,
  },
  terms: {
    byId: {},
    byDocumentId: {},
    search: '',
    selectedId: null,
  },
  viewerIds: [],
}

const slice = createSlice({
  name: 'documentCompare',
  initialState,
  reducers: {
    addDocuments(state, action: PayloadAction<ComparisonDocument[]>) {
      const documents = action.payload
      documents.forEach((document) => {
        if (!state.documents.allIds.includes(document.id)) {
          state.documents.allIds.push(document.id)
        }

        state.documents.byId[document.id] = document
      })

      const viewerAssignedDocumentIds = Object.values(state.documents.byViewerId)
      const unassignedDocuments = documents.filter(
        ({ id }) => !viewerAssignedDocumentIds.includes(id)
      )

      const { byViewerId } = state.documents
      const availableViewerCount = state.meta.maxViewerCount - Object.values(byViewerId).length

      take(unassignedDocuments, availableViewerCount).forEach((document) => {
        let viewerId = state.viewerIds.find((id) => !byViewerId[id])
        if (!viewerId) {
          viewerId = Utils.generateId()
          state.viewerIds.push(viewerId)
        }
        state.documents.byViewerId[viewerId] = document.id
      })
    },
    addViewer(state) {
      if (state.viewerIds.length < state.meta.maxViewerCount) {
        const { allIds, byViewerId } = state.documents
        const id = Utils.generateId()
        state.viewerIds.push(id)

        /**
         * If there are documents available that aren't assigned to a viewer, take the first
         * unassigned and assign it
         */
        if (allIds.length >= state.viewerIds.length) {
          const viewedIds = Object.values(byViewerId)
          const documentId = head(allIds.filter((id) => !viewedIds.includes(id)))
          state.documents.byViewerId[id] = documentId
        }
      }
    },
    removeDocuments(state, action: PayloadAction<string[]>) {
      const documentIdsToRemove = action.payload
      const { allIds, byId, byViewerId } = state.documents

      const viewersByDocumentId = Object.entries(byViewerId).reduce(
        (acc, [viewerId, documentId]) => ({ ...acc, [documentId]: viewerId }),
        {}
      )

      pullAll(allIds, documentIdsToRemove)

      documentIdsToRemove.forEach((documentId) => {
        delete byId[documentId]

        const associatedViewerId = viewersByDocumentId[documentId]
        if (associatedViewerId) {
          delete byViewerId[associatedViewerId]
        }
      })

      const unassociatedViewerIds = state.viewerIds.filter((viewerId) => !byViewerId[viewerId])
      if (unassociatedViewerIds.length) {
        const associatedDocumentIds = Object.values(byViewerId)
        const unassociatedDocumentIds = Object.keys(byId).filter(
          (documentId) => !associatedDocumentIds.includes(documentId)
        )

        // If there are documents that are not associated to a viewer then associate them
        if (unassociatedDocumentIds.length) {
          unassociatedDocumentIds
            .slice(0, unassociatedViewerIds.length)
            .forEach((documentId, index) => {
              byViewerId[unassociatedViewerIds[index]] = documentId
            })
          return
        }

        // Otherwise viewers need to be removed since there are no documents to associate
        state.viewerIds = state.viewerIds.filter((id) => !unassociatedViewerIds.includes(id))
      }
    },
    removeViewer(state, action: PayloadAction<string>) {
      state.viewerIds = state.viewerIds.filter((id) => id !== action.payload)
      delete state.documents.byViewerId[action.payload]
    },
    setKeyTerms(state, action: PayloadAction<IDocumentCompareState['keyTerms']>) {
      state.keyTerms.byDocumentId = action.payload.byDocumentId
      state.keyTerms.byId = action.payload.byId
      state.keyTerms.byTermId = action.payload.byTermId
    },
    setLoadingKeyTerms(state, action: PayloadAction<boolean>) {
      state.meta.loadingKeyTerms = action.payload
    },
    setSelectedDocument(state, action: PayloadAction<{ documentId: string; viewerId: string }>) {
      const { documentId, viewerId } = action.payload
      state.documents.byViewerId[viewerId] = documentId
    },
    setSelectedDocuments(state, action: PayloadAction<{ documentId: string; viewerId: string }[]>) {
      action.payload.forEach(
        ({ documentId, viewerId }) => (state.documents.byViewerId[viewerId] = documentId)
      )
    },
    setSelectedTermId(state, action: PayloadAction<string>) {
      state.terms.selectedId = action.payload
    },
    setSelectTermIdFromKeyTermId(state, action: PayloadAction<string>) {
      const {
        keyTerms: { byId },
      } = state
      const keyTerm = byId[action.payload]
      if (keyTerm) {
        state.terms.selectedId = getTermId(keyTerm.keyTerm)
      }
    },
    setTerms(
      state,
      action: PayloadAction<Pick<IDocumentCompareState['terms'], 'byDocumentId' | 'byId'>>
    ) {
      state.terms.byDocumentId = action.payload.byDocumentId
      state.terms.byId = action.payload.byId
    },
    setTermSearch(state, action: PayloadAction<string>) {
      state.terms.search = action.payload
    },
  },
})

// Helpers

const getTermId = ({ termTypeID, title }: IKeyTerm & { title: string }) => termTypeID || title

// Actions
const { setKeyTerms, setLoadingKeyTerms, setTerms } = slice.actions
export const {
  addDocuments,
  addViewer,
  removeDocuments,
  removeViewer,
  setSelectedDocument,
  setSelectedDocuments,
  setSelectedTermId,
  setSelectTermIdFromKeyTermId,
  setTermSearch,
} = slice.actions

interface IApiKeyTerm {
  id: string
  keyTerms: (IKeyTerm & { isAttribute: boolean; title: string })[]
}

export const getKeyTerms = createAsyncThunk(
  'documentCompare/getKeyTerms',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    // Fetch terms for every document currently in play: those tracked in allIds
    // AND those assigned to viewers (byViewerId). setSelectedDocument only writes
    // byViewerId, so relying on allIds alone misses viewer-only docs.
    const allIds = selectDocumentIds(state)
    const viewerIds = Object.values(state.documentCompare.documents.byViewerId).filter(
      (id): id is string => !!id
    )
    const ids = Array.from(new Set([...allIds, ...viewerIds].map(String)))
    if (ids.length) {
      dispatch(setLoadingKeyTerms(true))

      const rawKeyTerms: IApiKeyTerm[] = await APIClient.makeAPICall('POST', '/docs/terms', {
        body: { documentIds: ids, excludeFields: ['content', 'originalContent'] },
      })

      const keyTermsByDocumentId: Record<string, string[]> = {}
      const keyTermsById: Record<string, ComparisonKeyTerm> = {}
      // eslint-disable-next-line prefer-const
      let keyTermsByTermId: Record<string, string[]> = {}
      const termsById: Record<string, ComparisonTermType> = {}
      const termsByDocumentId: Record<string, string[]> = {}

      rawKeyTerms.forEach(({ id, keyTerms }) => {
        keyTerms.forEach((keyTerm) => {
          const termId = getTermId(keyTerm)
          if (termId) {
            keyTermsByTermId[termId] = (keyTermsByTermId[termId] ?? []).concat(keyTerm.id)
            termsById[termId] = {
              documentIds: (termsById[termId]?.documentIds ?? []).concat(id),
              id: termId,
              title: keyTerm.title,
            }
            termsByDocumentId[id] = (termsByDocumentId[id] ?? []).concat(termId)
          }

          keyTermsByDocumentId[id] = (keyTermsByDocumentId[id] ?? []).concat(keyTerm.id)
          keyTermsById[keyTerm.id] = {
            documentId: id,
            keyTerm,
            annotation: {
              highlightColor: keyTerm.highlightColor,
              id: keyTerm.id,
              page: keyTerm.pdfPageNumber,
              quads: keyTerm.quads,
              title: keyTerm.title,
            },
          }
        })
      })

      batch(() => {
        dispatch(
          setKeyTerms({
            byDocumentId: keyTermsByDocumentId,
            byId: keyTermsById,
            byTermId: keyTermsByTermId,
          })
        )
        dispatch(setTerms({ byDocumentId: termsByDocumentId, byId: termsById }))
      })
    }
  }
)

// Selectors
const selectRoot = (state: RootState) => state.documentCompare

const selectKeyTermsRoot = createSelector(selectRoot, ({ keyTerms }) => keyTerms)

const selectDocumentsRoot = createSelector(selectRoot, ({ documents }) => documents)
const selectDocumentsAllIds = createSelector(selectDocumentsRoot, ({ allIds }) => allIds)
const selectDocumentsByIds = createSelector(selectDocumentsRoot, ({ byId }) => byId)
const selectDocumentsByViewerId = createSelector(
  selectDocumentsRoot,
  ({ byViewerId }) => byViewerId
)

const selectTermsRoot = createSelector(selectRoot, ({ terms }) => terms)

const selectTermsByDocumentId = createSelector(selectTermsRoot, ({ byDocumentId }) => byDocumentId)

const selectTermsById = createSelector(selectTermsRoot, ({ byId }) => byId)

const selectViewerIds = createSelector(selectRoot, ({ viewerIds }) => viewerIds)

const selectMeta = createSelector(selectRoot, ({ meta }) => meta)

export const selectMaxViewerCount = createSelector(
  selectMeta,
  ({ maxViewerCount }) => maxViewerCount
)

export const selectDocumentIds = createSelector(selectDocumentsAllIds, (allIds) => allIds)

export const selectDocumentCount = createSelector(selectDocumentsAllIds, (allIds) => allIds.length)

export const selectSelectedTermId = createSelector(selectTermsRoot, ({ selectedId }) => selectedId)

export const selectDocuments = createSelector(
  selectDocumentsAllIds,
  selectDocumentsByIds,
  (allIds, byId) =>
    sortBy(
      allIds.map((id) => byId[id]),
      'title'
    )
)

/**
 * Returns all terms and their selected document count, sorted alphabetically by title, for the
 * documents that are shown in the viewers.
 */
export const selectSelectedDocumentsTerms = createSelector(
  selectTermsByDocumentId,
  selectTermsById,
  selectDocumentsByViewerId,
  (termsByDocumentId, termsById, byViewerId) => {
    const selectedDocumentIds = Object.values(byViewerId)
    const selectedDocumentsTerms = selectedDocumentIds
      .flatMap((documentId) => termsByDocumentId[documentId])
      .filter((termId) => termId)
      .map((termId) => {
        const term = termsById[termId]
        return {
          ...term,
          documentIds: term.documentIds.filter((id) => selectedDocumentIds.includes(id)),
        }
      })

    return sortBy(uniqBy(selectedDocumentsTerms, 'id'), 'title')
  }
)

export const selectSelectedTermKeyTermIds = createSelector(
  selectSelectedTermId,
  selectKeyTermsRoot,
  (selectedTermId, keyTerms) =>
    (keyTerms.byTermId[selectedTermId] ?? []).reduce((acc, keyTermId) => {
      const { documentId } = keyTerms.byId[keyTermId]
      return { ...acc, [documentId]: keyTermId }
    }, {})
)

export const selectSelectedDocumentsAnnotations = createSelector(
  selectDocumentsByViewerId,
  selectKeyTermsRoot,
  (byViewerId, keyTerms) =>
    Object.values(byViewerId).reduce((acc, documentId) => {
      return {
        ...acc,
        [documentId]: (keyTerms.byDocumentId[documentId] ?? []).map(
          (keyTermId) => keyTerms.byId[keyTermId].annotation
        ),
      }
    }, {}) as Record<string, ComparisonKeyTerm['annotation'][]>
)

export const selectViewerCount = createSelector(selectViewerIds, (viewerIds) => viewerIds.length)

export const selectEmptyViewerCount = createSelector(
  selectDocumentsByViewerId,
  (byViewerId) => Object.values(byViewerId).filter((d) => !d).length
)

export const selectSelectedDocuments = createSelector(
  selectDocumentsByIds,
  selectDocumentsByViewerId,
  (byIds, byViewerId) =>
    Object.values(byViewerId)
      .filter((d) => d)
      .reduce((acc, documentId) => acc.concat(byIds[documentId]), [])
)

export const selectViewerDocumentMap = createSelector(
  selectDocumentsByIds,
  selectDocumentsByViewerId,
  selectViewerIds,
  (byId, byViewerId, viewerIds) =>
    viewerIds.reduce(
      (acc, viewerId) => ({ ...acc, [viewerId]: byId[byViewerId[viewerId]] }),
      {}
    ) as Record<string, ComparisonDocument>
)

export const selectMinViewerCount = createSelector(
  selectMeta,
  ({ minViewerCount }) => minViewerCount
)

export const selectTermSearch = createSelector(selectTermsRoot, ({ search }) => search)

// Reduce
export default slice.reducer
